import { userUsesSelfElectedPublicFundingOptions } from "@outschool/funding-programs-shared";
import { EsaSessionType, useSession } from "@outschool/ui-auth";

const ARIZONA_ABBREVIATION = "AZ";

function isArizonaAbbreviation(value: unknown): boolean {
  return (
    typeof value === "string" && value.toUpperCase() === ARIZONA_ABBREVIATION
  );
}

function readFundingStateFromMetadata(metadata: unknown): string | undefined {
  if (!metadata || typeof metadata !== "object") {
    return undefined;
  }

  if (!("fundingState" in metadata)) {
    return undefined;
  }

  const fundingState = (metadata as { fundingState?: unknown }).fundingState;
  return typeof fundingState === "string" ? fundingState : undefined;
}

type PathfinderCurrentUser = Pick<
  NonNullable<ReturnType<typeof useSession>["currentUser"]>,
  "esaMembershipPrograms" | "selfElectedStateFundingProgram" | "children"
>;

export function isEligibleAzEsaNonClassWalletUser({
  currentUser,
  isLoggedIn,
  esaSessionType,
}: {
  currentUser: PathfinderCurrentUser | null;
  isLoggedIn: boolean;
  esaSessionType: EsaSessionType | null;
}): boolean {
  if (esaSessionType === EsaSessionType.CW) {
    return false;
  }

  // Pathfinder targeting is logged-in only.
  if (!isLoggedIn || !currentUser) {
    return false;
  }

  const hasAzMembershipProgram = Boolean(
    currentUser.esaMembershipPrograms?.some(program =>
      isArizonaAbbreviation(program),
    ),
  );

  const hasAzSelfElectedState = isArizonaAbbreviation(
    currentUser.selfElectedStateFundingProgram?.stateAbbreviation,
  );
  const hasSelfElectedPublicFunding = userUsesSelfElectedPublicFundingOptions(
    currentUser.selfElectedStateFundingProgram,
  );
  const hasAzSelfElectedPublicFunding =
    hasAzSelfElectedState && hasSelfElectedPublicFunding;

  const hasAzLearnerFundingProgram = Boolean(
    currentUser.children?.some(child =>
      child?.fundingPrograms?.some(fundingProgram => {
        if (!fundingProgram || fundingProgram.deactivatedAt) {
          return false;
        }

        const slug = fundingProgram.fundingProgram?.slug;
        const fundingState =
          fundingProgram.fundingProgram?.publishedVersion?.fundingState;
        const metadataFundingState = readFundingStateFromMetadata(
          fundingProgram.metadata,
        );

        return (
          isArizonaAbbreviation(slug) ||
          isArizonaAbbreviation(fundingState) ||
          isArizonaAbbreviation(metadataFundingState)
        );
      }),
    ),
  );

  return (
    hasAzMembershipProgram ||
    hasAzSelfElectedPublicFunding ||
    hasAzLearnerFundingProgram
  );
}

export function useAzEsaPathfinderEligibility() {
  const { currentUser, isLoggedIn, esaSessionType } = useSession();

  return {
    isEligibleAzEsaNonClassWalletUser: isEligibleAzEsaNonClassWalletUser({
      currentUser,
      isLoggedIn,
      esaSessionType,
    }),
  };
}
